import { Alert, Tabs } from 'antd'
import React, { FunctionComponent, useEffect, useMemo } from 'react'

import { LABEL_TAB_KEY, LabelDetail } from '../../utils/labelsTypes'
import { labelTabSlug, TabItem, tabsProps } from '../../utils/tabsHelpers'
import { useTabKeyState } from '../../utils/useTabKeyState'
import { AuctionLabelTable } from '../AuctionLabelTable/AuctionLabelTable'
import { CeeMonitorTable } from '../CeeMonitorTable/CeeMonitorTable'
import { CuzkDocumentCollectionTable } from '../CuzkDocumentCollectionTable/CuzkDocumentCollectionTable'
import { CuzkEpoTable } from '../CuzkEpoTable/CuzkEpoTable'
import { CuzkLvTable } from '../CuzkLvTable/CuzkLvTable'
import { CuzkMonitorRcTable } from '../CuzkMonitorRcTable/CuzkMonitorRcTable'
import { LabelDetailPageExtra } from '../LabelDetailPageExtra/LabelDetailPageExtra'
import { LvDetailDrawer } from '../LvDetailDrawer/LvDetailDrawer'
import { PageSkeleton } from '../PageSkeleton/PageSkeleton'
import { PostServiceRecipientListTable } from '../PostServiceRecipientListTable/PostServiceRecipientListTable'
import { PriceEstimateTable } from '../PriceEstimateTable/PriceEstimateTable'
import { PropertyDetailDrawer } from '../PropertyDetailDrawer/PropertyDetailDrawer'
import { PropertyLabelTable } from '../PropertyLabelTable/PropertyLabelTable'

export interface LabelOverviewProps {
  labelId: string
  connections: {
    [key: string]: string[]
  }
  loading: boolean
  label: LabelDetail
}

export const LabelOverview: FunctionComponent<LabelOverviewProps> = props => {
  const defaultActiveKey = useMemo(() => {
    for (const key of Object.keys(labelTabSlug)) {
      if (props.connections[key]?.length) {
        return key as LABEL_TAB_KEY
      }
    }
    return 'unassigned'
  }, [props.connections])

  const [activeTabKey, setActiveTabKey] = useTabKeyState<LABEL_TAB_KEY>(
    defaultActiveKey,
    labelTabSlug
  )
  useEffect(() => {
    if (activeTabKey === 'unassigned' && defaultActiveKey !== 'unassigned') {
      setActiveTabKey(defaultActiveKey)
    }
  }, [defaultActiveKey, activeTabKey, setActiveTabKey])

  const tabItems = useMemo<TabItem<LABEL_TAB_KEY>[]>(
    () => [
      ...(activeTabKey === 'unassigned'
        ? [
            {
              key: 'unassigned' as const,
              label: 'Nepřiřazeno',
              disabled: false,
              className: 'bg-white p-6',
              children: (
                <Alert
                  type="warning"
                  message="Prozatím jste štítek nepřiřadili k žádnému záznamu."
                ></Alert>
              ),
            },
          ]
        : []),
      {
        key: 'propertyLabelId',
        label: 'Nemovitosti',
        className: 'bg-white p-6',
        disabled: !props.connections.propertyLabelId?.length,
        children: (
          <>
            <PropertyLabelTable labelId={props.labelId} />
            <PropertyDetailDrawer />
            <LvDetailDrawer />
          </>
        ),
      },
      {
        key: 'auctionListingId',
        label: 'Dražby',
        className: 'bg-white p-6',
        disabled: !props.connections?.auctionListingId?.length,
        children: (
          <AuctionLabelTable
            auctionListingFilter={{
              id: { in: props.connections?.auctionListingId },
            }}
          />
        ),
      },
      {
        key: 'monitoringCuzkRcId',
        label: 'ČÚZK Osoby',
        className: 'bg-white p-6',
        disabled: !props.connections.monitoringCuzkRcId?.length,
        children: (
          <CuzkMonitorRcTable
            MonitoringCuzkRcFilter={{
              id: { in: props.connections?.monitoringCuzkRcId },
            }}
          />
        ),
      },
      {
        key: 'monitoringCeeId',
        label: 'CEE',
        className: 'bg-white p-6',
        disabled: !props.connections.monitoringCeeId?.length,
        children: (
          <CeeMonitorTable
            monitoringCeeFilter={{
              id: { in: props.connections.monitoringCeeId },
            }}
          />
        ),
      },
      {
        key: 'postserviceListId',
        label: 'Seznamy adresátů',
        title: 'Hromadná pošta',
        className: 'bg-white p-6',
        disabled: !props.connections.postserviceListId?.length,
        children: (
          <div className="bg-white py-2">
            <PostServiceRecipientListTable
              idFilter={{ in: props.connections.postserviceListId }}
            />
          </div>
        ),
      },
      {
        key: 'monitId',
        label: 'Cenové odhady',
        className: 'bg-white p-6',
        disabled: !props.connections.monitId?.length,
        children: (
          <>
            <PriceEstimateTable
              monitFilter={{ id: { in: props.connections.monitId } }}
            />
            <PropertyDetailDrawer />
          </>
        ),
      },
      {
        key: 'cuzkEpoId',
        label: 'Evidence práv pro osobu',
        className: 'bg-white p-6',
        disabled: !props.connections.cuzkEpoId?.length,
        children: (
          <CuzkEpoTable
            refetchState={false}
            idFilter={{ in: props.connections.cuzkEpoId }}
          />
        ),
      },
      {
        key: 'cuzkLvId',
        label: 'Listy vlastnictví',
        className: 'bg-white p-6',
        disabled: !props.connections.cuzkLvId?.length,
        children: (
          <CuzkLvTable
            refetchState={false}
            idFilter={{ in: props.connections.cuzkLvId }}
          />
        ),
      },
      {
        key: 'cuzkDocumentCollectionId',
        label: 'Sbírka listin',
        className: 'bg-white p-6',
        disabled: !props.connections.cuzkDocumentCollectionId?.length,
        children: (
          <CuzkDocumentCollectionTable
            idFilter={{ in: props.connections.cuzkDocumentCollectionId }}
          />
        ),
      },
    ],
    [
      activeTabKey,
      props.connections.propertyLabelId?.length,
      props.connections?.auctionListingId,
      props.connections.monitoringCuzkRcId,
      props.connections.monitoringCeeId,
      props.connections.postserviceListId,
      props.connections.monitId,
      props.connections.cuzkEpoId,
      props.connections.cuzkLvId,
      props.connections.cuzkDocumentCollectionId,
      props.labelId,
    ]
  )

  if (props.loading) {
    return <PageSkeleton />
  }
  return (
    <Tabs
      {...tabsProps}
      items={tabItems}
      activeKey={activeTabKey}
      onTabClick={setActiveTabKey}
      tabBarExtraContent={<LabelDetailPageExtra label={props.label} />}
    />
  )
}
